import { useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  MessageSquare,
  Search,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  ToggleLeft,
  ToggleRight,
  CalendarRange,
} from 'lucide-react';
import {
  useMessages,
  useCreateMessage,
  useUpdateMessage,
  useDeleteMessage,
  type Message,
  type MessageInput,
} from '@/hooks/useMessages';
import { ApiValidationError } from '@/lib/api';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function nowPlusHours(h: number) {
  const d = new Date();
  d.setHours(d.getHours() + h, 0, 0, 0);
  return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM" for datetime-local inputs
}

const EMPTY_FORM: MessageInput = {
  descricao: '',
  dataInicio: nowPlusHours(1),
  dataFim: nowPlusHours(25),
  estado: 'Ativo',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Convert RFC3339 → datetime-local input value (YYYY-MM-DDTHH:MM) */
function toDatetimeLocal(iso: string) {
  return new Date(iso).toISOString().slice(0, 16);
}

/** Convert datetime-local value → RFC3339 */
function toRFC3339(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}

// ─── Validation ───────────────────────────────────────────────────────────────

type FormErrors = Partial<Record<keyof MessageInput, string>>;

function validate(form: MessageInput): FormErrors {
  const e: FormErrors = {};
  if (!form.descricao.trim()) {
    e.descricao = 'A descrição da mensagem é obrigatória.';
  } else if ([...form.descricao].length > 150) {
    e.descricao = 'Máximo de 150 caracteres.';
  }
  if (!form.dataInicio) e.dataInicio = 'A data de início é obrigatória.';
  if (!form.dataFim) e.dataFim = 'A data de fim é obrigatória.';
  if (form.dataInicio && form.dataFim && form.dataFim <= form.dataInicio) {
    e.dataFim = 'A data de fim deve ser posterior à data de início.';
  }
  return e;
}

// ─── Estado badge ─────────────────────────────────────────────────────────────

function EstadoBadge({ estado }: { estado: string }) {
  return estado === 'Ativo' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      Ativo
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      Inativo
    </span>
  );
}

// ─── Form Field ───────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}

function FormField({ label, error, required, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground/60">{hint}</p>}
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Create / Edit Modal ──────────────────────────────────────────────────────

interface MessageModalProps {
  mode: 'create' | 'edit';
  initial?: Message;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function MessageModal({ mode, initial, onClose, onSuccess, onError }: MessageModalProps) {
  const create = useCreateMessage();
  const update = useUpdateMessage();

  const [form, setForm] = useState<MessageInput>(
    initial
      ? {
          descricao: initial.descricao,
          dataInicio: toDatetimeLocal(initial.dataInicio),
          dataFim: toDatetimeLocal(initial.dataFim),
          estado: initial.estado,
        }
      : EMPTY_FORM,
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverErrors, setServerErrors] = useState<FormErrors>({});

  const isPending = create.isPending || update.isPending;
  const fieldError = (key: keyof MessageInput) => errors[key] || serverErrors[key];

  const set = <K extends keyof MessageInput>(key: K, value: MessageInput[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    if (serverErrors[key]) setServerErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: MessageInput = {
      ...form,
      descricao: form.descricao.trim(),
    };
    const clientErrors = validate(trimmed);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    // Convert datetime-local → RFC3339 for the API
    const payload: MessageInput = {
      ...trimmed,
      dataInicio: toRFC3339(trimmed.dataInicio),
      dataFim: toRFC3339(trimmed.dataFim),
    };

    try {
      if (mode === 'create') {
        await create.mutateAsync(payload);
        onSuccess('Mensagem criada com sucesso.');
      } else {
        await update.mutateAsync({ id: initial!.id, data: payload });
        onSuccess('Mensagem actualizada com sucesso.');
      }
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setServerErrors(err.errors as FormErrors);
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro desconhecido.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="msg-modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            <h2 id="msg-modal-title" className="text-lg font-semibold text-foreground">
              {mode === 'create' ? 'Nova Mensagem' : 'Editar Mensagem'}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form id="msg-form" onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-4 overflow-y-auto">
          {/* Descrição */}
          <FormField label="Descrição" required error={fieldError('descricao')} hint="Máximo de 150 caracteres.">
            <textarea
              className={`input resize-none h-24 ${fieldError('descricao') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.descricao}
              onChange={e => set('descricao', e.target.value)}
              maxLength={150}
              placeholder="Escreve a mensagem para os participantes…"
              aria-invalid={!!fieldError('descricao')}
              autoFocus
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground/50 tabular-nums">
                {[...form.descricao].length}/150
              </span>
            </div>
          </FormField>

          {/* Datas */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Data de início" required error={fieldError('dataInicio')}>
              <input
                type="datetime-local"
                className={`input ${fieldError('dataInicio') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.dataInicio}
                onChange={e => set('dataInicio', e.target.value)}
                aria-invalid={!!fieldError('dataInicio')}
              />
            </FormField>

            <FormField label="Data de fim" required error={fieldError('dataFim')}>
              <input
                type="datetime-local"
                className={`input ${fieldError('dataFim') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.dataFim}
                onChange={e => set('dataFim', e.target.value)}
                min={form.dataInicio}
                aria-invalid={!!fieldError('dataFim')}
              />
            </FormField>
          </div>

          {/* Estado toggle */}
          <div className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/10">
            <div>
              <p className="text-sm font-medium text-foreground">Mensagem Ativa</p>
              <p className="text-xs text-muted-foreground">
                Mensagens inativas não são apresentadas aos participantes.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.estado === 'Ativo'}
              onClick={() => set('estado', form.estado === 'Ativo' ? 'Inativo' : 'Ativo')}
              className={`transition-colors ${form.estado === 'Ativo' ? 'text-primary' : 'text-muted-foreground'}`}
            >
              {form.estado === 'Ativo'
                ? <ToggleRight className="w-8 h-8" />
                : <ToggleLeft className="w-8 h-8" />
              }
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="msg-form"
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'create' ? 'Criar Mensagem' : 'Guardar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Dialog ────────────────────────────────────────────────────────────

interface DeleteDialogProps {
  message: Message;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function DeleteDialog({ message, onClose, onSuccess, onError }: DeleteDialogProps) {
  const del = useDeleteMessage();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const confirm = async () => {
    if (!reason.trim()) {
      setReasonError('O motivo de eliminação é obrigatório.');
      return;
    }
    try {
      await del.mutateAsync({ id: message.id, reason: reason.trim() });
      onSuccess('Mensagem eliminada com sucesso.');
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError && err.errors['reason']) {
        setReasonError(err.errors['reason']);
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro ao eliminar.');
    }
  };

  const preview = [...message.descricao].slice(0, 60).join('');
  const previewLabel = message.descricao.length > 60 ? preview + '…' : preview;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="del-msg-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <h3 id="del-msg-title" className="text-base font-semibold text-foreground">
              Eliminar Mensagem
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Tens a certeza que queres eliminar a mensagem{' '}
              <span className="font-semibold text-foreground">"{previewLabel}"</span>?
              O registo ficará arquivado.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Motivo de eliminação<span className="text-destructive ml-0.5">*</span>
          </label>
          <textarea
            className={`input resize-none h-20 ${reasonError ? 'border-destructive focus:ring-destructive/30' : ''}`}
            value={reason}
            onChange={e => { setReason(e.target.value); if (reasonError) setReasonError(''); }}
            placeholder="Descreve o motivo de eliminação…"
            aria-invalid={!!reasonError}
          />
          {reasonError && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              {reasonError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={del.isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={del.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50 transition-colors"
          >
            {del.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

interface DetailPanelProps {
  message: Message;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function DetailPanel({ message, onClose, onEdit, onDelete }: DetailPanelProps) {
  const isDeleted = !!message.deletedAt;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" aria-label="Detalhe da mensagem">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-md bg-card shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground leading-tight">Detalhe da Mensagem</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                <EstadoBadge estado={message.estado} />
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar painel"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Deleted banner */}
          {isDeleted && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-destructive">Registo eliminado</p>
                <p className="text-xs text-destructive/80 mt-0.5">{message.deletionReason}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Em {formatDate(message.deletedAt!)}
                </p>
              </div>
            </div>
          )}

          {/* Descrição */}
          <div className="space-y-1.5">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Descrição</p>
            <p className="text-sm text-foreground whitespace-pre-line">{message.descricao}</p>
          </div>

          {/* Período de publicação */}
          <div className="space-y-1.5">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium flex items-center gap-1">
              <CalendarRange className="w-3 h-3" />
              Período de publicação
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg border border-border bg-muted/10">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Início</p>
                <p className="text-xs text-foreground">{formatDate(message.dataInicio)}</p>
              </div>
              <div className="p-3 rounded-lg border border-border bg-muted/10">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Fim</p>
                <p className="text-xs text-foreground">{formatDate(message.dataFim)}</p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Criada em</p>
              <p className="text-xs text-foreground">{formatDate(message.createdAt)}</p>
            </div>
            <div className="p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Actualizada em</p>
              <p className="text-xs text-foreground">{formatDate(message.updatedAt)}</p>
            </div>
          </div>

          {/* UUID */}
          <div className="p-3 rounded-lg border border-border bg-muted/10">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">ID</p>
            <p className="text-[11px] font-mono text-muted-foreground break-all">{message.id}</p>
          </div>
        </div>

        {/* Footer — hidden for deleted records */}
        {!isDeleted && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-border shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={onDelete}
              className="flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-md border border-destructive/30 bg-destructive/5 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ search, onCreate }: { search: string; onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
        <MessageSquare className="w-7 h-7 text-muted-foreground/40" />
      </div>
      {search ? (
        <>
          <p className="text-base font-medium text-muted-foreground">
            Nenhum resultado para <span className="text-foreground">"{search}"</span>
          </p>
          <p className="text-sm text-muted-foreground/60">Tenta pesquisar por outro texto.</p>
        </>
      ) : (
        <>
          <p className="text-base font-medium text-muted-foreground">Sem mensagens registadas</p>
          <button
            onClick={onCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Criar primeira mensagem
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; message: Message }
  | { type: 'delete'; message: Message };

export default function MensagensPage() {
  const { toasts, toast, dismiss } = useToast();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState<'all' | 'active' | 'inactive'>('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  const { data, isLoading, isError, error, isFetching } = useMessages({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch,
    activeOnly: filterEstado === 'active',
    showDeleted,
  });

  const messages = data?.messages ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);

  const closeModal = () => setModal({ type: 'none' });

  const hasFilters = !!debouncedSearch || filterEstado !== 'all';

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setFilterEstado('all');
    setPage(0);
  };

  return (
    <>
      <Helmet>
        <title>Mensagens | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-primary" />
              Mensagens
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestão das mensagens informativas publicadas para os participantes.
            </p>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm shrink-0"
          >
            <Plus className="w-4 h-4" />
            Nova Mensagem
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative min-w-56 max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              placeholder="Pesquisar por descrição…"
              className="input pl-9 w-full"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              aria-label="Pesquisar mensagens"
            />
          </div>

          {/* Estado filter */}
          <div className="flex items-center gap-1 p-1 rounded-md border border-border bg-muted/30">
            {(['all', 'active', 'inactive'] as const).map(v => (
              <button
                key={v}
                onClick={() => { setFilterEstado(v); setPage(0); }}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  filterEstado === v
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {v === 'all' ? 'Todas' : v === 'active' ? 'Ativas' : 'Inativas'}
              </button>
            ))}
          </div>

          {/* Show deleted toggle */}
          <button
            onClick={() => { setShowDeleted(v => !v); setPage(0); }}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
              showDeleted
                ? 'border-destructive/40 bg-destructive/10 text-destructive'
                : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
            }`}
            aria-pressed={showDeleted}
          >
            {showDeleted ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showDeleted ? 'Ocultar eliminadas' : 'Mostrar eliminadas'}
          </button>

          {/* Count + clear */}
          <div className="flex items-center gap-2 ml-auto">
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
              >
                Limpar filtros
              </button>
            )}
            {total > 0 && (
              <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                {total} mensagem{total !== 1 ? 'ns' : ''}
                {isFetching && !isLoading && (
                  <Loader2 className="inline-block w-3 h-3 ml-1.5 animate-spin text-primary" />
                )}
              </span>
            )}
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="card overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <AlertTriangle className="w-8 h-8 text-destructive" />
              <p className="text-sm font-medium text-destructive">
                {error?.message ?? 'Erro ao carregar mensagens.'}
              </p>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {messages.length === 0 ? (
                <EmptyState search={debouncedSearch} onCreate={() => setModal({ type: 'create' })} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm" aria-label="Lista de mensagens">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Descrição</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Período</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Estado</th>
                        <th scope="col" className="px-4 py-3 w-20 text-right font-medium text-muted-foreground">Acções</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {messages.map(m => {
                        const isDeletedRow = !!m.deletedAt;
                        return (
                          <tr
                            key={m.id}
                            className={`hover:bg-accent/30 transition-colors cursor-pointer ${isDeletedRow ? 'opacity-60' : ''}`}
                            onClick={() => setSelectedMessage(m)}
                          >
                            {/* Descrição */}
                            <td className="px-4 py-3 max-w-xs">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                                  <MessageSquare className="w-4 h-4 text-primary" />
                                </div>
                                <p className="font-medium text-foreground truncate">{m.descricao}</p>
                              </div>
                            </td>

                            {/* Período */}
                            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                              <div className="flex items-center gap-1 text-xs">
                                <span>{formatDate(m.dataInicio)}</span>
                                <span className="text-muted-foreground/40">→</span>
                                <span>{formatDate(m.dataFim)}</span>
                              </div>
                            </td>

                            {/* Estado */}
                            <td className="px-4 py-3">
                              {isDeletedRow
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">Eliminada</span>
                                : <EstadoBadge estado={m.estado} />
                              }
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div
                                className="flex items-center gap-1 justify-end"
                                onClick={e => e.stopPropagation()}
                              >
                                {!isDeletedRow && (
                                  <>
                                    <button
                                      onClick={() => setModal({ type: 'edit', message: m })}
                                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                                      title="Editar mensagem"
                                      aria-label="Editar mensagem"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setModal({ type: 'delete', message: m })}
                                      className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                      title="Eliminar mensagem"
                                      aria-label="Eliminar mensagem"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Pagination ── */}
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {from}–{to} de {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      aria-label="Página anterior"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let p: number;
                        if (totalPages <= 5) p = i;
                        else if (page < 3) p = i;
                        else if (page >= totalPages - 3) p = totalPages - 5 + i;
                        else p = page - 2 + i;
                        return (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            aria-label={`Página ${p + 1}`}
                            aria-current={p === page ? 'page' : undefined}
                            className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                              p === page
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            }`}
                          >
                            {p + 1}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      aria-label="Próxima página"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Próximo
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Detail Panel ── */}
      {selectedMessage && (
        <DetailPanel
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          onEdit={() => {
            setModal({ type: 'edit', message: selectedMessage });
            setSelectedMessage(null);
          }}
          onDelete={() => {
            setModal({ type: 'delete', message: selectedMessage });
            setSelectedMessage(null);
          }}
        />
      )}

      {/* ── Modals ── */}
      {modal.type === 'create' && (
        <MessageModal
          mode="create"
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'edit' && (
        <MessageModal
          mode="edit"
          initial={modal.message}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'delete' && (
        <DeleteDialog
          message={modal.message}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}

      {/* ── Toasts ── */}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
